import { useState, useCallback, useEffect, useRef } from "react";
import { Link, NavLink } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "state/currentUser/currentUserActions";
import { logoutLocationSelector } from "state/currentUser/currentUserSelectors";
import { getHeaderLinks } from "lib";
import _PATHS from "routes/paths";
import EasilIcon from "views/components/icons/EasilIcon";
import PersonIcon from "views/components/icons/PersonIcon";
import ExitStandardisedIcon from "views/components/icons/ExitStandardisedIcon";
import SimpleCartIcon from "views/components/icons/SimpleCartIcon";
import CircledQuestionMarkIcon from "views/components/icons/CircledQuestionMarkIcon";
import { Avatar } from "views/components";
import NotificationContainer from "views/containers/NotificationContainer/NotificationContainer";
import style from "./MobilePageHeader.module.css";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PATHS = _PATHS as any;

interface Team {
  id: string;
  name: string;
  logoUrl?: string;
  parentPathArr?: string[];
}

interface MobilePageHeaderProps {
  team: Team;
  teams: Team[];
  totalTeamCount: number;
  onSwitchTeam: (args: { teamId: string }) => void;
  onCreateDesign: () => void;
  onToggleHelpWindow: () => void;
  onSearchTeams: (args: { name: string }) => void;
  onClearSearch: () => void;
  isFetchingTeams: boolean;
  isSearchMode: boolean;
  pathToCatalogue: string;
  isAdmin: boolean;
  isBrandManager: boolean;
  allowEasilTemplates: boolean;
  isTeamTemplates: boolean;
  url: string;
  currentOrder?: { id: string; lineItems?: unknown[] };
}

const MobilePageHeader = ({
  team,
  teams,
  totalTeamCount,
  onSwitchTeam,
  onCreateDesign,
  onToggleHelpWindow,
  onSearchTeams,
  onClearSearch,
  isFetchingTeams,
  isSearchMode,
  pathToCatalogue,
  isAdmin,
  isBrandManager,
  allowEasilTemplates,
  isTeamTemplates,
  url,
  currentOrder
}: MobilePageHeaderProps) => {
  const dispatch = useDispatch();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentUser = useSelector((state: any) => state.currentUser);
  const logoutLocation = useSelector(logoutLocationSelector);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTeamExpanded, setIsTeamExpanded] = useState(false);
  const [isTemplatesExpanded, setIsTemplatesExpanded] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const [menuTop, setMenuTop] = useState(0);
  const [teamSearchQuery, setTeamSearchQuery] = useState("");
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => {
      if (prev) {
        // Closing menu - reset expanded sections
        setIsTeamExpanded(false);
        setIsTemplatesExpanded(false);
        setTeamSearchQuery("");
        onClearSearch();
      }
      return !prev;
    });
  }, [onClearSearch]);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    setIsTeamExpanded(false);
    setIsTemplatesExpanded(false);
    setTeamSearchQuery("");
    onClearSearch();
  }, [onClearSearch]);

  const handleLogout = useCallback(() => {
    closeMenu();
    dispatch(logout());
  }, [closeMenu, dispatch]);

  // Redirect after logout
  useEffect(() => {
    if (logoutLocation) {
      window.location.href = logoutLocation;
    }
  }, [logoutLocation]);

  // Close menu on escape key
  useEffect(() => {
    if (!isMenuOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMenuOpen, closeMenu]);

  // Lock body scroll and compute menu position when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
      if (headerRef.current) {
        const rect = headerRef.current.getBoundingClientRect();
        setMenuTop(rect.bottom);
      }
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const headerLinks = getHeaderLinks({
    pathToCatalogue,
    isAdmin,
    isBrandManager,
    allowEasilTemplates,
    isTeamTemplates
  });

  const isOrganisation =
    team && team.parentPathArr && team.parentPathArr.length <= 1;

  const handleTeamSearch = useCallback(
    (query: string) => {
      setTeamSearchQuery(query);
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      if (query.trim()) {
        searchDebounceRef.current = setTimeout(() => {
          onSearchTeams({ name: query.trim() });
        }, 300);
      } else {
        onClearSearch();
      }
    },
    [onSearchTeams, onClearSearch]
  );

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handleTeamSwitch = useCallback(
    (switchTeam: Team) => {
      onSwitchTeam({ teamId: switchTeam.id });
      closeMenu();
    },
    [onSwitchTeam, closeMenu]
  );

  const handleCreateDesign = useCallback(() => {
    closeMenu();
    onCreateDesign();
  }, [closeMenu, onCreateDesign]);

  const handleHelp = useCallback(() => {
    closeMenu();
    onToggleHelpWindow();
  }, [closeMenu, onToggleHelpWindow]);

  const cartItemCount =
    currentOrder && currentOrder.lineItems
      ? (currentOrder.lineItems as unknown[]).length
      : 0;

  return (
    <>
      <div
        ref={headerRef}
        className={style.header}
        data-testid="mobile-page-header"
      >
        <div className={style.leftSection}>
          <NavLink
            to={PATHS.workspace}
            className={style.iconButton}
            aria-label="Home"
          >
            {/* @ts-expect-error JS icon component lacks proper TS types */}
            <EasilIcon color="white" size="28" />
          </NavLink>
        </div>

        <div className={style.centerSection}>
          <NotificationContainer wrapperClassName={style.notification} />
        </div>

        <div className={style.rightSection}>
          <button
            className={style.hamburgerButton}
            onClick={toggleMenu}
            aria-label="Menu"
            aria-expanded={isMenuOpen}
            data-testid="mobile-page-hamburger"
          >
            <div className={style.hamburgerLines}>
              <div className={style.hamburgerLine} />
              <div className={style.hamburgerLine} />
              <div className={style.hamburgerLine} />
            </div>
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <>
          <div
            className={style.overlay}
            onClick={closeMenu}
            data-testid="mobile-page-menu-overlay"
          />
          <div
            className={style.menuPanel}
            style={menuTop ? { top: `${menuTop}px` } : undefined}
            data-testid="mobile-page-menu"
          >
            {/* Team picker */}
            {teams && totalTeamCount > 1 && (
              <div className={style.teamSection}>
                <button
                  className={style.teamButton}
                  onClick={() => setIsTeamExpanded(prev => !prev)}
                  data-testid="mobile-page-team-picker"
                >
                  {/* @ts-expect-error JS component has loosely typed props */}
                  <Avatar
                    size="32px"
                    theme="light"
                    src={team.logoUrl}
                    name={team.name}
                    singleCharacterNameInitial
                    noBorder
                  />
                  <span className={style.teamName}>{team.name}</span>
                  <span
                    className={style.teamCaret}
                    data-expanded={isTeamExpanded}
                  />
                </button>
                {isTeamExpanded && (
                  <>
                    {totalTeamCount > 5 && (
                      <input
                        className={style.teamSearchInput}
                        type="text"
                        placeholder="Search teams..."
                        value={teamSearchQuery}
                        onChange={e => handleTeamSearch(e.target.value)}
                        autoFocus
                        data-testid="mobile-page-team-search"
                      />
                    )}
                    <div className={style.teamList}>
                      {isFetchingTeams && (
                        <div className={style.teamListLoading}>
                          Searching...
                        </div>
                      )}
                      {!isFetchingTeams &&
                        isSearchMode &&
                        teams.length === 0 && (
                          <div className={style.teamListLoading}>
                            No teams found
                          </div>
                        )}
                      {teams.map((t: Team) => (
                        <div
                          key={t.id}
                          className={style.teamListItem}
                          data-active={t.id === team.id}
                          onClick={() => handleTeamSwitch(t)}
                        >
                          {/* @ts-expect-error JS component has loosely typed props */}
                          <Avatar
                            size="24px"
                            theme="light"
                            src={t.logoUrl}
                            name={t.name}
                            singleCharacterNameInitial
                            noBorder
                          />
                          <span className={style.teamListItemName}>
                            {t.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Create Design */}
            <button
              className={style.createDesignButton}
              onClick={handleCreateDesign}
              data-testid="mobile-page-create-design"
            >
              Create Design
            </button>

            {/* Nav links */}
            {headerLinks.designs?.visible && (
              <Link
                to={headerLinks.designs.url}
                className={style.menuItem}
                data-active={url.startsWith(PATHS.workspace)}
                onClick={closeMenu}
              >
                My Workspace
              </Link>
            )}
            {headerLinks.templates?.visible &&
              (headerLinks.templates.isDropdown ? (
                <div className={style.templatesSection}>
                  <button
                    className={style.menuItem}
                    onClick={() => setIsTemplatesExpanded(prev => !prev)}
                    data-testid="mobile-page-templates-toggle"
                  >
                    Templates
                    <span
                      className={style.templatesCaret}
                      data-expanded={isTemplatesExpanded}
                    />
                  </button>
                  {isTemplatesExpanded &&
                    headerLinks.templates.dropdownOptions.map(
                      (option: { label: string; path: string }) => (
                        <Link
                          key={option.path}
                          to={option.path}
                          className={style.templateSubLink}
                          data-active={url.startsWith(option.path)}
                          onClick={closeMenu}
                        >
                          {option.label}
                        </Link>
                      )
                    )}
                </div>
              ) : (
                <Link
                  to={headerLinks.templates.url}
                  className={style.menuItem}
                  data-active={url.startsWith(PATHS.catalogue)}
                  onClick={closeMenu}
                >
                  Templates
                </Link>
              ))}
            {headerLinks.brand?.visible && (
              <Link
                to={headerLinks.brand.url}
                className={style.menuItem}
                data-active={url.startsWith(PATHS.brand)}
                onClick={closeMenu}
              >
                Brand
              </Link>
            )}
            {headerLinks.team?.visible && (
              <Link
                to={headerLinks.team.url}
                className={style.menuItem}
                data-active={url.startsWith(PATHS.team)}
                onClick={closeMenu}
              >
                Team
              </Link>
            )}

            <div className={style.menuDivider} />

            {/* Secondary links */}
            {isOrganisation && isAdmin && (
              <Link
                to={PATHS.subscription}
                className={style.menuItem}
                data-active={url.startsWith(PATHS.subscription)}
                onClick={closeMenu}
              >
                Subscription
              </Link>
            )}
            <Link
              to={PATHS.orders}
              className={style.menuItem}
              data-active={url.startsWith(PATHS.orders)}
              onClick={closeMenu}
            >
              My Orders
            </Link>
            <div
              className={style.menuItem}
              onClick={handleHelp}
              data-testid="mobile-page-help"
            >
              {/* @ts-expect-error JS icon component lacks proper TS types */}
              <CircledQuestionMarkIcon size="18" color="white" />
              Help
            </div>
            <Link
              to={PATHS.cart}
              className={style.menuItem}
              data-active={url.startsWith(PATHS.cart)}
              onClick={closeMenu}
              data-testid="mobile-page-cart"
            >
              {/* @ts-expect-error JS icon component lacks proper TS types */}
              <SimpleCartIcon color="white" size="18" />
              Cart
              {cartItemCount > 0 && (
                <span className={style.cartBadge}>{cartItemCount}</span>
              )}
            </Link>

            {/* User section */}
            <div className={style.userSection}>
              {currentUser && (
                <div
                  className={style.userDetails}
                  data-testid="mobile-page-user"
                >
                  {/* @ts-expect-error JS component has loosely typed props */}
                  <Avatar
                    size="32px"
                    src={currentUser.avatarUrl}
                    name={currentUser.name}
                    noBorder
                  />
                  <div className={style.userMeta}>
                    <div className={style.userName}>{currentUser.name}</div>
                    <div className={style.userEmail}>{currentUser.email}</div>
                  </div>
                </div>
              )}
              <Link
                to={PATHS.settings}
                className={style.menuItem}
                onClick={closeMenu}
                data-testid="mobile-page-profile"
              >
                {/* @ts-expect-error JS icon component has loosely typed props */}
                <PersonIcon width="16" height="16" strokeWidth={3} />
                Profile
              </Link>
              <div
                className={style.menuItem}
                onClick={handleLogout}
                data-testid="mobile-page-logout"
              >
                {/* @ts-expect-error JS icon component has loosely typed props */}
                <ExitStandardisedIcon width="16" height="16" />
                Logout
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MobilePageHeader;
